import { getLang } from '@/lib/lang'
import { blocksUiFor } from '@/data/blocks.en'
import { shopLive, ShopPreparing } from '../../lib/shopLive'
/** Source: public/design-patterns.html D-4a. */
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import printfulProductsJson from '../../data/printful-products.json'
import shopCatalogJson from '../../data/shop-catalog.json'
import { getPageContent } from '../../lib/pageContent'
import { BlockPicture, Yen } from './helpers'

type Good = {
  href?: string
  image?: string
  alt?: string
  title?: string
  name?: string
  yen?: number
  yenMax?: number
  colors?: string
  text?: string
  badge?: string
  category?: string
  provisional?: boolean
}

type ProductImage = { image: string; alt?: string }
type PrintfulProduct = {
  id: number
  slug: string
  name: string
  category?: string
  priceMin: number
  priceMax: number
  currency: string
  images: ProductImage[]
}
type CatalogEntry = {
  title?: string
  alt?: string
  category?: string
  order?: number
}

const printfulProducts = printfulProductsJson as PrintfulProduct[]
const shopCatalog = shopCatalogJson as Record<string, CatalogEntry>
const allFilterLabels = new Set(['すべて', 'すべての商品', '全商品', 'All'])

function displayName(product: PrintfulProduct, catalogEntry?: CatalogEntry) {
  return catalogEntry?.title?.trim() || product.name.replace(/^Queen's Butler\s*/, '')
}

function buildPrintfulGoods(): Good[] {
  return printfulProducts
    .map((product, sourceIndex) => {
      const catalogEntry = shopCatalog[String(product.id)]
      const firstImage = product.images[0]
      return {
        item: {
          href: `/shop/items/${product.slug}/`,
          image: firstImage?.image,
          alt: catalogEntry?.alt || firstImage?.alt || displayName(product, catalogEntry),
          title: displayName(product, catalogEntry),
          yen: product.priceMin,
          yenMax: product.priceMax,
          category: catalogEntry?.category || product.category,
        } satisfies Good,
        order: catalogEntry?.order ?? Number.MAX_SAFE_INTEGER,
        sourceIndex,
      }
    })
    .sort((a, b) => a.order - b.order || a.sourceIndex - b.sourceIndex)
    .map(({ item }) => item)
}

export function Goods({
  filters = [],
  items,
  limit,
  eyebrow,
  heading,
  note,
}: {
  filters?: string[]
  items?: Good[]
  limit?: number
  eyebrow?: string
  heading?: string
  note?: string
}) {
  const { pathname } = useLocation()
  const t = blocksUiFor(getLang(pathname))
  const page = getPageContent(pathname)
  const usesPrintfulCatalog = items === undefined && page?.service === 'shop'
  const sourceItems = useMemo(
    () => usesPrintfulCatalog ? buildPrintfulGoods() : (items ?? []),
    [items, usesPrintfulCatalog],
  )
  const effectiveFilters = useMemo(() => {
    if (!usesPrintfulCatalog) return filters
    const categories = sourceItems
      .map((item) => item.category)
      .filter((category): category is string => Boolean(category))
    return [t.all, ...new Set(categories)]
  }, [filters, sourceItems, usesPrintfulCatalog, t.all])
  const [active, setActive] = useState(
    effectiveFilters.find((filter) => allFilterLabels.has(filter)) ?? t.all,
  )
  const [sort, setSort] = useState('new')
  const visible = useMemo(() => {
    const filtered = allFilterLabels.has(active)
      ? sourceItems
      : sourceItems.filter((item) => item.category === active)
    const sorted = sort === 'asc'
      ? [...filtered].sort((a, b) => (a.yen ?? Infinity) - (b.yen ?? Infinity))
      : sort === 'desc'
        ? [...filtered].sort((a, b) => (b.yen ?? -Infinity) - (a.yen ?? -Infinity))
        : filtered
    return typeof limit === 'number' ? sorted.slice(0, Math.max(0, limit)) : sorted
  }, [active, limit, sort, sourceItems])

  if (usesPrintfulCatalog && !shopLive) return <div className='shell sec tight'>{eyebrow && <div className='eyebrow'>{eyebrow}</div>}{heading && <h2 className='h-disp h-2'>{heading}</h2>}<ShopPreparing /></div>
  return <div className="shell sec tight">
    {eyebrow && <div className="eyebrow">{eyebrow}</div>}
    {heading && <h2 className="h-disp h-2">{heading}</h2>}
    {sourceItems.length === 0 ? (
      <p className="def">{t.goodsEmpty}</p>
    ) : <>
      <div className="shop-tools">
        <div>{effectiveFilters.map((filter) => (
          <button
            type="button"
            className={`tag${active === filter ? ' on' : ''}`}
            onClick={() => setActive(filter)}
            key={filter}
          >{filter}</button>
        ))}</div>
        <div>{t.sort} <select aria-label={t.sort} value={sort} onChange={(event) => setSort(event.target.value)}>
          <option value="new">{t.sortNew}</option>
          <option value="asc">{t.sortAsc}</option>
          <option value="desc">{t.sortDesc}</option>
        </select></div>
      </div>
      {visible.length === 0 ? (
        <p className="def">{t.goodsNoMatch}</p>
      ) : <div className="goods">{visible.map((item, index) => (
        <a href={item.href ?? '#'} key={`${item.title ?? item.name}-${index}`}>
          <div className="ph"><BlockPicture src={item.image} alt={item.alt} /></div>
          {item.badge && <span className="tag on">{item.badge}</span>}
          <div className="nm">{item.title ?? item.name}</div>
          {typeof item.yen === 'number' && (
            <div className="pr">
              <Yen value={item.yen} suffix={typeof item.yenMax === 'number' && item.yenMax > item.yen ? '円〜(税込)' : '円(税込)'} />
            </div>
          )}
          {item.provisional && <div className="cl">{t.provisional}</div>}
          {item.text && <div className="cl">{item.text}</div>}
          {item.colors !== undefined && <div className="cl">{item.colors}</div>}
        </a>
      ))}</div>}
    </>}
    {note && <p className="note" style={{ marginTop: 28 }}>{note}</p>}
  </div>
}
